import {
  AddOutlined,
  PageDS,
  Spacer,
  TabMenu,
  ToastContainerDS,
  triggerToastDS,
  dismissToastDS,
  updateToastDS,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnalyticsVehiclesTab } from "./advancedAnalytics/advancedAnalyticsVehiclesTab";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  driverPlanningsSlice,
  selectDriverPlanningsStatus,
  selectDriverPlanningsUploadProgress,
} from "./driverPlannings/driverPlanningsSlice";
import { ImportDriverPlanningsModal } from "./driverPlannings/importDriverPlanningsModal";
import { HistoryTab } from "./history/historyTab";
import { MonthlyReportsTab } from "./monthlyReports/monthlyReportsTab";
import "../../../i18n/i18n";
import { supportPhonesByCompanyGroupUuid } from "../../../constants/support";
import { selectManagersList } from "../users/usersSlice";
import { selectCompany } from "../homeSlice";
import {
  advancedAnalyticVehiclesAsync,
  selectAdvancedAnalyticsVehiclesListStatus,
} from "./fleetSlice";

export const Fleet = () => {
  const { t } = useTranslation();
  const firstPath = location.pathname.split("/")[1];
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const params = useParams();
  const company = useAppSelector(selectCompany);
  const managers = useAppSelector(selectManagersList);
  const advancedAnalyticListVehiclesStatus = useAppSelector(
    selectAdvancedAnalyticsVehiclesListStatus,
  );

  const otherSupportPhones = useMemo(
    () =>
      managers && managers.length > 0 && managers[0].companyGroup
        ? supportPhonesByCompanyGroupUuid[managers[0].companyGroup]
        : undefined,
    [managers],
  );

  const [selectedTab, setSelectedTab] = useState<string>(
    params.tab ?? "advanced_analytics",
  );
  const driverPlanningsStatus = useAppSelector(selectDriverPlanningsStatus);
  const uploadProgress = useAppSelector(selectDriverPlanningsUploadProgress);
  const [isModalPlanningVisible, setIsModalPlanningVisible] = useState(false);
  const processingToastIdRef = useRef<number | string | null>(null);

  type EnvType = "production" | "staging" | "demo" | "development";
  const env = import.meta.env["VITE_TARGET_ENV"] as EnvType;
  const showMonthlyReportsTab = env !== "demo" && env !== "production";

  const tabs = useMemo(() => {
    const base = [
      {
        name: "advanced_analytics",
        title: t("fleet.tab.advanced_analytics"),
      },
      {
        name: "history",
        title: t("fleet.tab.tripsHistory"),
      },
    ];
    if (showMonthlyReportsTab) {
      base.push({
        name: "monthly_reports",
        title: t("fleet.tab.monthly_reports"),
      });
    }
    return base;
  }, [t, showMonthlyReportsTab]);

  useEffect(() => {
    if (!showMonthlyReportsTab && selectedTab === "monthly_reports") {
      setSelectedTab("advanced_analytics");
    }
  }, [showMonthlyReportsTab, selectedTab]);

  useEffect(() => {
    if (selectedTab) {
      navigate(
        `/${firstPath}/fleet/${selectedTab}${
          params.id !== undefined ? `/${params.id}` : ""
        }`,
      );
    }
  }, [navigate, params.id, selectedTab, firstPath]);

  const cta = {
    title: t("trips.cta"),
    action: () => {
      setIsModalPlanningVisible(true);
    },
    leftIcon: <AddOutlined />,
  };

  useEffect(() => {
    if (company && advancedAnalyticListVehiclesStatus === "idle") {
      void dispatch(advancedAnalyticVehiclesAsync(company.uuid));
    }
  }, [dispatch, company, advancedAnalyticListVehiclesStatus]);

  useEffect(() => {
    if (company && !company.hasAdvancedAnalytics) {
      navigate(`/home`);
    }
  }, [company, navigate]);

  useEffect(() => {
    if (driverPlanningsStatus === "success") {
      // Fermer le toast "processing" s'il existe
      if (processingToastIdRef.current) {
        dismissToastDS(processingToastIdRef.current);
        processingToastIdRef.current = null;
      }
      triggerToastDS(t("trips.importPlanning.success") || "", "SUCCESS");
      dispatch(driverPlanningsSlice.actions.resetPlanningUploadStatus());
    } else if (driverPlanningsStatus === "failed") {
      // Fermer le toast "processing" s'il existe
      if (processingToastIdRef.current) {
        dismissToastDS(processingToastIdRef.current);
        processingToastIdRef.current = null;
      }
      triggerToastDS(t("trips.importPlanning.failure") || "", "ERROR");
      dispatch(driverPlanningsSlice.actions.resetPlanningUploadStatus());
    }
  }, [dispatch, driverPlanningsStatus, t]);

  useEffect(() => {
    if (driverPlanningsStatus === "processing") {
      if (!processingToastIdRef.current) {
        const toastId = triggerToastDS(
          t("trips.importPlanning.processing") || "",
          "DOWNLOADING",
          { autoClose: false },
        );
        if (toastId) {
          processingToastIdRef.current = toastId;
        }
      } else {
        updateToastDS(
          processingToastIdRef.current,
          t("trips.importPlanning.processing") || "",
          "DOWNLOADING",
          uploadProgress,
        );
      }
    }
  }, [driverPlanningsStatus, t, uploadProgress]);

  return (
    <>
      <ImportDriverPlanningsModal
        visible={isModalPlanningVisible}
        onCloseModal={() => {
          setIsModalPlanningVisible(false);
        }}
      />
      {!isModalPlanningVisible && (
        <PageDS
          title={t("fleet.title")}
          toaster={<ToastContainerDS />}
          cta={selectedTab === "history" ? cta : undefined}
          topElement={
            <>
              <TabMenu
                tabs={tabs}
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
              />
              <Spacer y={0.5} />
            </>
          }
          supportButton={{
            showMail: !otherSupportPhones,
            otherPhones: otherSupportPhones,
          }}
        >
          <AnalyticsVehiclesTab />
          <HistoryTab />
          <MonthlyReportsTab />
        </PageDS>
      )}
    </>
  );
};
